'use client';

import { CSSProperties } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage as Image } from '../..';
import { addBtn, addBtnGrey2, SleepGraph } from './icons';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import styles from './DesktopSleepComponent.module.css';

export const DesktopSleepComponent = ({
  className,
  handleSleepScore,
  handleSleepStage,
  handleSleepContributor,
  handleAdvancedMetrics,
}: {
  className?: string;
  handleSleepScore?: any;
  handleSleepStage?: any;
  handleSleepContributor?: any;
  handleAdvancedMetrics?: any;
}) => {
  const t = useTranslations('ring');
  return (
    <section
      className={className ? `${styles.root} ${className}` : styles.root}
      aria-labelledby="sleep-section-heading"
      aria-label={t('desktopSleepComponent.section.ariaLabel')}
    >
      <header className="top">
        <h2 id="sleep-section-heading">
          {t('ring.bentoSleepSection.head.a')}
          <br />
          {t('ring.bentoSleepSection.head.b')}
        </h2>
        <p>{t('ring.bentoSleepSection.subhead')}</p>
      </header>

      <article
        className="top-left"
        role="region"
        aria-labelledby="sleep-score-heading"
        style={
          {
            '--top-left-bg': `url(${getCompressedAssetUrl(
              '/web_v2/ring-buy/sleep/png/sleepScoreBg.png/w_1200',
            )})`,
          } as CSSProperties
        }
      >
        <div className="content">
          <h4 id="sleep-score-heading">
            {t('ring.bentoSleepSection.sleepCard.title')}
          </h4>
          <h3>{t('ring.bentoSleepSection.sleepCard.subtitle')}</h3>
          <div
            className="img-container"
            role="img"
            aria-label={t('desktopSleepComponent.content.ariaLabel')}
          >
            <Image
              src={'web_v2/ring-buy/sleep/svg/sleepIndex.svg'}
              fill
              alt=""
              className="sleep-index"
              aria-hidden="true"
            />
          </div>
        </div>
        <button
          className="expand"
          onClick={handleSleepScore}
          aria-label={t('desktopSleepComponent.button.ariaLabel.learnMoreAbout')}
          aria-describedby="sleep-score-heading"
          type="button"
        >
          <span className="sr-only">
            {t('desktopSleepComponent.text.expandSleepScore')}
          </span>
          {addBtnGrey2}
        </button>
      </article>

      <article
        className="top-right"
        role="region"
        aria-labelledby="sleep-contributors-heading"
      >
        <div className="content">
          <h4 id="sleep-contributors-heading">
            {t('ring.bentoSleepSection.sleepContributorsCard.title')}
          </h4>
          <h3>
            {t('ring.bentoSleepSection.sleepContributorsCard.subtitleA')}{' '}
            <br />{' '}
            {t('ring.bentoSleepSection.sleepContributorsCard.subtitleB')}
          </h3>
          <div
            className="img-container"
            role="img"
            aria-label={t('desktopSleepComponent.content.ariaLabel2')}
          >
            <Image
              src={'web_v2/ring-buy/sleep/png/sleepContrbutorAsset.png'}
              fill
              alt=""
              className="contributorImg"
              aria-hidden="true"
            />
          </div>
        </div>
        <button
          className="expand dark-hover"
          onClick={handleSleepContributor}
          aria-label={t(
            'desktopSleepComponent.button.ariaLabel.learnMoreAbout2',
          )}
          aria-describedby="sleep-contributors-heading"
          type="button"
        >
          <span className="sr-only">
            {t('desktopSleepComponent.text.expandSleepContributors')}
          </span>
          {addBtn}
        </button>
      </article>

      <article
        className="bottom-left"
        role="region"
        aria-labelledby="advanced-metrics-heading"
      >
        <div className="content">
          <h4 id="advanced-metrics-heading">
            {t('ring.bentoSleepSection.advancedMetricsCard.title')}
          </h4>
          <h3>
            {t('ring.bentoSleepSection.advancedMetricsCard.subtitleA')} <br />{' '}
            {t('ring.bentoSleepSection.advancedMetricsCard.subtitleB')}
          </h3>
        </div>
        <div
          className="img-container"
          role="img"
          aria-label={t('desktopSleepComponent.content.ariaLabel3')}
        >
          <Image
            src={'web_v2/ring-buy/sleep/png/advMbg.png'}
            fill
            alt=""
            className="metrics-bg"
            aria-hidden="true"
          />
        </div>
        <button
          className="expand"
          onClick={handleAdvancedMetrics}
          aria-label={t(
            'desktopSleepComponent.button.ariaLabel.learnMoreAbout3',
          )}
          aria-describedby="advanced-metrics-heading"
          type="button"
        >
          <span className="sr-only">
            {t('desktopSleepComponent.text.expandAdvancedMetrics')}
          </span>
          {addBtnGrey2}
        </button>
      </article>

      <article
        className="bottom-right"
        role="region"
        aria-labelledby="sleep-stages-heading"
      >
        <div className="content">
          <h4 id="sleep-stages-heading">
            {t('ring.bentoSleepSection.sleepStagesCard.title')}
          </h4>
          <h3>{t('ring.bentoSleepSection.sleepStagesCard.subtitleA')}</h3>
          <div
            className="sleep-graph"
            role="img"
            aria-label={t('desktopSleepComponent.content.ariaLabel4')}
          >
            <span className="sr-only">
              {t('desktopSleepComponent.text.sleepStagesGraph')}
            </span>
            {SleepGraph}
          </div>
        </div>
        <ul
          className="points"
          aria-label={t('desktopSleepComponent.text.sleepStagesLegend')}
        >
          <li className="point">
            <span className="sr-only">
              {t('desktopSleepComponent.text.stage')}{' '}
            </span>
            {t('ring.bentoSleepSection.sleepStagesCard.bulletA')}
          </li>
          <li className="point">
            <span className="sr-only">
              {t('desktopSleepComponent.text.stage')}{' '}
            </span>
            {t('ring.bentoSleepSection.sleepStagesCard.bulletB')}
          </li>
          <li className="point">
            <span className="sr-only">
              {t('desktopSleepComponent.text.stage')}{' '}
            </span>
            {t('ring.bentoSleepSection.sleepStagesCard.bulletC')}
          </li>
          <li className="point">
            <span className="sr-only">
              {t('desktopSleepComponent.text.stage')}{' '}
            </span>
            {t('ring.bentoSleepSection.sleepStagesCard.bulletD')}
          </li>
        </ul>
        <button
          className="expand"
          onClick={handleSleepStage}
          aria-label={t(
            'desktopSleepComponent.button.ariaLabel.learnMoreAbout4',
          )}
          aria-describedby="sleep-stages-heading"
          type="button"
        >
          <span className="sr-only">
            {t('desktopSleepComponent.text.expandSleepStages')}
          </span>
          {addBtnGrey2}
        </button>
      </article>
    </section>
  );
};
